import { GridSortModel } from "@mui/x-data-grid";
import toast from "react-hot-toast";
import merge from "ts-deepmerge";
import { ApiException, FileResponse } from "../../api";
import dayjs, { ConfigType } from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export function getEnumKeys<TEnum extends object>(enumToDeconstruct: TEnum): Array<keyof typeof enumToDeconstruct> {
  return Object.keys(enumToDeconstruct) as Array<keyof typeof enumToDeconstruct>;
};

export function dayjsUtc(config?: ConfigType, format?: string, strict?: boolean): dayjs.Dayjs {
  return dayjs.utc(config, format, strict);
}

export function dayjsFormat(value: any, format: string = "YYYY-MM-DD HH:mm:ss") {
  return value ? dayjs(value).format(format) : "";
}

export function dayjsUtcFormat(value: any, format: string = "YYYY-MM-DD HH:mm:ss") {
  return value ? dayjs(value).utc().format(format) : "";
}

type NotificationType = "default" | "success" | "error" | "warning" | "info";

function isStringArray(o: any): boolean {
  return o && Array.isArray(o) && o.length > 0 && typeof o[0] === "string";
}

async function errmsg(err: any): Promise<string> {
  try {
    if (err instanceof ApiException) {
      const response = (err as ApiException).response as any;
      if (response instanceof Blob) {
        const b = response as Blob;
        const text = await b.text();
        if (text.startsWith("{") && text.endsWith("}")) {
          const json = JSON.parse(text);
          if (json?.message) return json.message;
        }
      }
    }
  } catch (err) {
    //
  }
  if (typeof err === "string") {
    if (err.startsWith("{") && err.endsWith("}")) {
      try {
        return errmsg(JSON.parse(err));
      } catch {
        // DO NOTHING
      }
    }
    return err;
  }
  if (isStringArray(err)) return (err as string[]).join("\n");
  if (err.isApiException === true) {
    if (typeof err.response === "string") return err.response;
  }
  if (typeof err?.error?.description === "string") return err?.error?.description;
  const errors = err?.error;
  if (errors && Array.isArray(errors) && errors.length > 0) {
    if (typeof errors[0]?.description === "string") return errors[0].description;
  }
  const message = err?.response?.message ?? err?.message ?? "Unknown error";
  if (isStringArray(message)) {
    return (message as string[]).join("\n");
  }
  return message;
}

export function notif(message: any = "", type: NotificationType = "default"): void {
  const iconMap = {
    default: "✨",
    success: "✔️",
    error: "❌",
    warning: "⚠️",
    info: "📖",
  };
  errmsg(message).then(str => 
    toast(str, {
      icon: iconMap[type as keyof typeof iconMap],
      style: {
        borderRadius: "10px",
        background: "#333",
        color: "#fff",
      },
    }));
}

export function getSortString(items: GridSortModel): string {
  const sort: string[] = [];
  if (items && items.length > 0) {
    for (const item of items) {
      if (item.sort === "asc") {
        sort.push(item.field);
      } else if (item.sort === "desc") {
        sort.push("-" + item.field);
      }
    }
  }
  return sort.join(",");
}

export function durationText(durationSeconds: number): string {
  const seconds = durationSeconds % 60;
  const minutes = Math.floor(durationSeconds / 60);
  if (minutes > 0) {
    return `${minutes} min ${seconds} sec`;
  }
  return `${seconds} sec`;
}

export function firstLetterUppercase(value: string): string {
  if (typeof value === "string" && value.length > 1) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}

export type StateReducerAction<T extends object> = { state?: Partial<T> | null; replace?: boolean } | null;
export type StateReducer<T extends object> = (state: T | null, action: StateReducerAction<T>) => T | null;

export function createStateReducer<T extends object>(): StateReducer<T> {
  return (state: T | null, action: StateReducerAction<T>): T | null => {
    if (action?.state) {
      if (!state || action?.replace) state = {} as T;
      // return merge(JSON.parse(JSON.stringify(state)), JSON.parse(JSON.stringify(action.state))) as T;
      return merge.withOptions({ mergeArrays: false }, JSON.parse(JSON.stringify(state)), JSON.parse(JSON.stringify(action.state))) as T;
    }
    return null;
  };
}

export function isExcel(extensionOrFileName: string): boolean {
  if (extensionOrFileName) {
    if (extensionOrFileName === "xls" || extensionOrFileName === "xlsx") return true;
    if (extensionOrFileName.indexOf(".") > 0) {
      const ext = extensionOrFileName.split(".").pop();
      return ext === "xls" || ext === "xlsx";
    }
  }
  return false;
}

export function removeArrayItem<T>(items: T[], predicate: (value: T, index: number, obj: T[]) => boolean): void {
  if (items && predicate) {
    const index = items.findIndex(predicate);
    if (index >= 0) {
      items.splice(index, 1);
    }
  }
}

export function isNotEmpty(value: string): boolean {
  return value !== null && value !== undefined && typeof value === "string" && value.length > 0;
}

export function isEmpty(value: string): boolean {
  return !isNotEmpty(value);
}

export function isDefined(value: any): boolean {
  return value !== null && value !== undefined;
}

export function arrayNotEmpty<T>(items?: T[]): boolean {
  return items !== undefined && Array.isArray(items) && items.length > 0;
}

export function arrayIsEmpty<T>(items?: T[]): boolean {
  return !arrayNotEmpty(items);
}

export function arrayContains<T>(items?: T[], value?: T): boolean {
  return items !== undefined && value !== undefined && Array.isArray(items) && items.includes(value);
}

export function createDownloadLink(file: FileResponse, window: any) {
  if (file && file.data) {
    const document = window.document;
    const url = window.URL.createObjectURL(file.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.fileName ?? "");
    document.body.appendChild(link);
    link.click();
  }
}

export function tryParseInt(value: string, defaultValue: number, radix?: number): number {
  try {
      if (typeof value === "number") return value as number;
      if (typeof value === "boolean") return (value as boolean) ? 1 : 0;
      if (typeof value === "string") {
          return parseInt(value, radix);
      }
  } catch (err) {
      // DO NOTHING
  }
  return defaultValue;
}
